import { useEffect } from 'react';
import type { ErrorBoundaryProps } from 'expo-router';
import { ErrorFallbackScreen } from '@kiips/ui';
import { reportError } from '../lib/observability';

/**
 * Per-route error boundary (Expo Router). Re-exported as `ErrorBoundary`
 * from a segment `_layout.tsx` so a render crash in that segment's screens
 * shows a recoverable fallback IN PLACE — keeping the rest of the app
 * (navigation shell, other tabs) alive — instead of falling through to the
 * single root boundary in `app/_layout.tsx`, which blanks everything.
 *
 * Renders the shared `ErrorFallbackScreen` (bare RN primitives, no Tamagui/
 * theme provider) so it survives a crash in a theme consumer. `retry` is
 * Expo Router's built-in remount of the route subtree. The error is
 * forwarded to observability — otherwise a route-level catch would swallow
 * it before the root `onError` reporter ever sees it.
 */
export function RouteErrorBoundary({ error, retry }: ErrorBoundaryProps) {
  useEffect(() => {
    reportError(error, { boundary: 'route' });
  }, [error]);

  return (
    <ErrorFallbackScreen
      title="This screen ran into a problem"
      message="You can try again, or go back and reopen it. The rest of the app is still working."
      actionLabel="Try again"
      onAction={() => void retry()}
    />
  );
}
